import { AlertTriangle, BarChart3, Clock3, MessageSquareText, QrCode, SmilePlus, TimerReset, TrendingUp } from "lucide-react";
import type { ReactNode } from "react";
import { formatApiError, type CsatTrendPoint, type HeatmapPoint } from "../api/client";
import { useAnalyticsComments, useAnalyticsOverview, useCsatTrend, useHeatmap, useNpsTrend, useRedFlags } from "../api/analytics";
import { SmartChart } from "../charts/SmartChart";

const accent = "#df1f66";
const accentStrong = "#ad0744";
const navy = "#223c5a";
const pale = "#edf2f7";

export function DashboardPage({ hotelId = "" }: { hotelId?: string }) {
  const filters = { hotelId };
  const overview = useAnalyticsOverview(filters);
  const npsTrend = useNpsTrend(filters);
  const csatTrend = useCsatTrend(filters);
  const heatmap = useHeatmap(filters);
  const redFlags = useRedFlags(filters);
  const comments = useAnalyticsComments(filters);
  const data = overview.data ?? {
    responsesToday: 0,
    responsesWeek: 0,
    csatAverage: 0,
    nps: 0,
    redFlags: 0,
    acknowledgeMinutesAverage: 0,
    mostCriticalLocation: null,
    bestLocation: null
  };
  const npsData = npsTrend.data ?? [];
  const csatData = csatTrend.data ?? [];
  const heatmapData = heatmap.data ?? [];
  const hotelBars = csatByHotel(heatmapData);
  const scoreDistribution = distributionFromCsat(csatData);
  const npsDistribution = distributionFromNps(npsData);
  const heatmapMatrix = matrixFromHeatmap(heatmapData);
  const queryError = overview.error ?? npsTrend.error ?? csatTrend.error ?? heatmap.error ?? redFlags.error ?? comments.error;

  return (
    <div className="page-stack executive-dashboard">
      {queryError && <p className="form-error">Dashboard analytics error: {formatApiError(queryError)}</p>}
      <section className="executive-kpi-grid">
        <MetricCard index="1" label="Danasnji odgovori" value={`${data.responsesToday}`} suffix={`/ ${data.responsesWeek} sedmicno`} icon={<MessageSquareText size={18} />} trend="+ live" />
        <MetricCard index="2" label="Prosjecni CSAT" value={data.csatAverage.toFixed(2)} suffix="/5.0" icon={<SmilePlus size={18} />} trend={csatData.at(-1)?.positiveRate ? `${csatData.at(-1)!.positiveRate.toFixed(0)}% pozitivnih` : "bez uzorka"} />
        <article className="executive-card nps-score-card">
          <div className="metric-card-head">
            <span>3. NPS rezultat</span>
            <QrCode size={18} />
          </div>
          <div className="nps-score-layout">
            <strong>{data.nps > 0 ? `+${data.nps}` : data.nps}</strong>
            <div className="mini-donut">
              <SmartChart type="doughnut" data={{
                labels: ["Promoters", "Passives", "Detractors"],
                datasets: [{ data: [npsDistribution.promoters, npsDistribution.passives, npsDistribution.detractors], backgroundColor: [accent, "#f0b64f", accentStrong], borderColor: "#fff", borderWidth: 3 }]
              }} options={{ cutout: "68%", plugins: { legend: { display: false }, tooltip: { enabled: true } } }} />
            </div>
            <ul>
              <li><i style={{ background: accent }} /> Promoters {npsDistribution.promoters}%</li>
              <li><i style={{ background: "#f0b64f" }} /> Passives {npsDistribution.passives}%</li>
              <li><i style={{ background: accentStrong }} /> Detractors {npsDistribution.detractors}%</li>
            </ul>
          </div>
        </article>
        <MetricCard index="4" label="Vrijeme do Ack." value={`${data.acknowledgeMinutesAverage.toFixed(1)} min`} suffix={data.redFlags ? `${data.redFlags} red flag` : "bez aktivnih"} icon={<Clock3 size={18} />} tone={data.redFlags ? "danger" : undefined} trend={data.mostCriticalLocation ?? "stabilno"} />
      </section>

      <section className="executive-grid charts-row">
        <article className="executive-card chart-panel">
          <PanelTitle title="NPS trend po danima" meta="Line chart / 30 dana" icon={<TrendingUp size={18} />} />
          <SmartChart type="line" data={{
            labels: npsData.map((point) => point.date),
            datasets: [{ data: npsData.map((point) => point.nps), borderColor: accent, backgroundColor: "rgba(223, 31, 102, 0.12)", pointBackgroundColor: accent, pointBorderColor: "#fff", pointRadius: 3, fill: true, tension: 0.4 }]
          }} options={executiveChartOptions({ suggestedMin: -100, suggestedMax: 100 })} />
        </article>
        <article className="executive-card chart-panel">
          <PanelTitle title="CSAT po hotelu / lokacijama" meta="Bar chart" icon={<BarChart3 size={18} />} />
          <SmartChart type="bar" data={{
            labels: hotelBars.map((point) => point.hotel),
            datasets: [{ data: hotelBars.map((point) => point.csat), backgroundColor: [accentStrong, accent, "#f06b98", navy], borderRadius: 10, maxBarThickness: 56 }]
          }} options={executiveChartOptions({ suggestedMin: 0, suggestedMax: 5 })} />
        </article>
      </section>

      <section className="executive-grid operations-row">
        <article className="executive-card heatmap-card">
          <PanelTitle title="Heatmap zadovoljstva" meta="Lokacije / kategorije" icon={<BarChart3 size={18} />} />
          <div className="heatmap-matrix" aria-label="Heatmap zadovoljstva po lokacijama i kategorijama">
            <span />
            {heatmapMatrix.columns.map((column) => <strong key={column}>{formatCategory(column)}</strong>)}
            {heatmapMatrix.rows.map((row) => (
              <HeatmapRow key={row.label} row={row} columns={heatmapMatrix.columns} />
            ))}
          </div>
        </article>
        <article className="executive-card distribution-card">
          <PanelTitle title="Distribucija ocjena 1-5" meta="CSAT kvalitet" icon={<SmilePlus size={18} />} />
          <SmartChart type="doughnut" data={{
            labels: ["1", "2", "3", "4", "5"],
            datasets: [{ data: scoreDistribution, backgroundColor: [accentStrong, "#c81758", accent, "#f06b98", navy], borderColor: pale, borderWidth: 3 }]
          }} options={{ responsive: true, maintainAspectRatio: false, plugins: { legend: { position: "bottom", labels: { boxWidth: 10, color: navy } } } }} />
        </article>
      </section>

      <section className="executive-grid lists-row">
        <article className="executive-card">
          <PanelTitle title="Red flag incidenti" meta="Operativni tracker" icon={<AlertTriangle size={18} />} />
          <div className="incident-list">
            {(redFlags.data ?? []).slice(0, 5).map((alert) => (
              <Incident key={alert.id} hotel={alert.hotel.name} location={alert.location.name} reason={alert.reason} />
            ))}
            {!(redFlags.data ?? []).length && <EmptyState text="Nema aktivnih red flag incidenata u izabranom periodu." />}
          </div>
        </article>
        <article className="executive-card">
          <PanelTitle title="Komentari sa sentimentom" meta="PII-safe prikaz" icon={<MessageSquareText size={18} />} />
          <div className="executive-comment-list">
            {(comments.data ?? []).slice(0, 5).map((comment) => (
              <div className="executive-comment" key={comment.id}>
                <strong>{comment.location}</strong>
                <span>{comment.subLocation ?? comment.unitCode ?? comment.requestPath ?? "segment"}</span>
                <p>{comment.commentRedacted ? "Komentar je sakriven za ovu rolu." : comment.comment}</p>
                <small className={comment.commentRedacted ? "badge muted" : "badge positive"}>{comment.commentRedacted ? "PII protected" : "Komentar"}</small>
              </div>
            ))}
            {!(comments.data ?? []).length && <EmptyState text="Nema komentara za prikaz." />}
          </div>
        </article>
      </section>
    </div>
  );
}

function csatByHotel(points: HeatmapPoint[]) {
  const grouped = new Map<string, { hotel: string; total: number; weighted: number }>();
  for (const point of points) {
    const current = grouped.get(point.hotel) ?? { hotel: point.hotel, total: 0, weighted: 0 };
    current.total += point.total;
    current.weighted += point.csat * point.total;
    grouped.set(point.hotel, current);
  }
  return [...grouped.values()].map((point) => ({
    hotel: point.hotel,
    csat: point.total ? point.weighted / point.total : 0
  }));
}

function distributionFromNps(points: Array<{ promoters: number; passives: number; detractors: number; total: number }>) {
  const totals = points.reduce((acc, point) => ({
    promoters: acc.promoters + point.promoters,
    passives: acc.passives + point.passives,
    detractors: acc.detractors + point.detractors,
    total: acc.total + point.total
  }), { promoters: 0, passives: 0, detractors: 0, total: 0 });
  if (!totals.total) return { promoters: 0, passives: 0, detractors: 0 };
  return {
    promoters: Math.round((totals.promoters / totals.total) * 100),
    passives: Math.round((totals.passives / totals.total) * 100),
    detractors: Math.round((totals.detractors / totals.total) * 100)
  };
}

function matrixFromHeatmap(points: HeatmapPoint[]) {
  const locationLabel = (point: HeatmapPoint) => `${point.hotel} / ${point.location}`;
  const rows = [...new Set(points.map(locationLabel))].slice(0, 5);
  const columns = [...new Set(points.map((point) => point.category))].slice(0, 4);
  const resolvedColumns = columns.length ? columns : ["service", "cleanliness", "food", "overall"];
  return {
    columns: resolvedColumns,
    rows: (rows.length ? rows : ["Restoran", "Sobe", "Wellness", "Plaza"]).map((label) => ({
      label,
      values: Object.fromEntries(resolvedColumns.map((category) => {
        const matches = points.filter((point) => locationLabel(point) === label && point.category === category);
        const total = matches.reduce((sum, point) => sum + point.total, 0);
        return [category, total ? matches.reduce((sum, point) => sum + point.csat * point.total, 0) / total : 0];
      }))
    }))
  };
}

function executiveChartOptions({ suggestedMin, suggestedMax }: { suggestedMin: number; suggestedMax: number }) {
  return {
    plugins: {
      legend: { display: false },
      tooltip: {
        backgroundColor: navy,
        titleColor: "#fff",
        bodyColor: "#fff",
        borderColor: accent,
        borderWidth: 1
      }
    },
    scales: {
      x: { grid: { display: false }, ticks: { color: "#9aa9b8", maxRotation: 0, autoSkip: true } },
      y: { suggestedMin, suggestedMax, grid: { color: "rgba(34,60,90,0.08)" }, ticks: { color: "#9aa9b8" } }
    }
  } as const;
}

function MetricCard({ index, label, value, suffix, icon, trend, tone }: { index: string; label: string; value: string; suffix: string; icon: ReactNode; trend: string; tone?: "danger" }) {
  return (
    <article className={tone === "danger" ? "executive-card metric-card danger" : "executive-card metric-card"}>
      <div className="metric-card-head">
        <span>{index}. {label}</span>
        {icon}
      </div>
      <div className="metric-card-value">
        <strong>{value}</strong>
        <small>{suffix}</small>
      </div>
      <em>{trend}</em>
    </article>
  );
}

function PanelTitle({ title, meta, icon }: { title: string; meta: string; icon: ReactNode }) {
  return (
    <div className="executive-panel-title">
      <div>
        <h2>{title}</h2>
        <span>{meta}</span>
      </div>
      {icon}
    </div>
  );
}

function HeatmapRow({ row, columns }: { row: { label: string; values: Record<string, number> }; columns: string[] }) {
  return (
    <>
      <b>{row.label}</b>
      {columns.map((column) => {
        const value = row.values[column] ?? 0;
        return <i key={column} className={heatmapTone(value)} title={`${row.label} / ${column}: ${value.toFixed(2)}`}>{value ? value.toFixed(1) : "-"}</i>;
      })}
    </>
  );
}

function heatmapTone(value: number) {
  if (value >= 4.3) return "great";
  if (value >= 3.6) return "good";
  if (value >= 2.8) return "warn";
  return "risk";
}

function formatCategory(category: string) {
  return category
    .replace(/_/g, " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function EmptyState({ text }: { text: string }) {
  return <p className="empty-state">{text}</p>;
}

function distributionFromCsat(points: CsatTrendPoint[]) {
  return [0, 1, 2, 3, 4].map((index) => points.reduce((sum, point) => sum + (point.scoreDistribution?.[index] ?? 0), 0));
}

function Incident({ hotel, location, reason }: { hotel: string; location: string; reason: string }) {
  return (
    <div className="incident">
      <TimerReset size={18} />
      <strong>{hotel}</strong>
      <span>{location}</span>
      <code>{reason}</code>
    </div>
  );
}
